// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'
import type { LiteralUnion } from 'type-fest'

import type { QdsFocusEventDetail } from '../../helpers'
import { isOverflowing, pickFocusEventAttributes } from '../../helpers'
import { CUSTOM_ERROR_FLAGS, NO_ERROR_FLAGS, VALID_STATE } from '../controls'
import type { Importance, Size } from '../shared'

export type Action = 'dropdown-close' | 'dropdown' | 'next'
export type ButtonImportance =
  | Importance
  | 'destructive'
  | 'subdued-destructive'
export type ButtonSize = Size | 'hero'
export type ButtonTarget = '_blank' | '_parent' | '_self' | '_top'
export type ButtonType = 'button' | 'reset' | 'submit'
export type FormMethod = 'dialog' | 'get' | 'post'

const isCounter = (value: unknown): value is number =>
  typeof value === 'string'
    ? !Number.isNaN(Number.parseFloat(value))
    : typeof value === 'number'

const isIndicator = (value: unknown): value is '' | true =>
  value === '' || value === true

/**
 * The `<qds-button>` element is an interactive element activated by a user
 * with a mouse, keyboard, finger, voice command, or other assistive technology.
 * Once activated, it then performs a programmable action, such as submitting a
 * [form](https://developer.mozilla.org/docs/Learn/Forms)
 * or opening a dialog.
 *
 * @see https://quartz.se.com/build/components/button
 */
@Component({
  tag: 'qds-button',
  formAssociated: true,
  shadow: { delegatesFocus: true },
  styleUrl: 'button.css',
})
export class Button implements ComponentInterface {
  /**
   * The name of the action icon to render.
   */
  @Prop() public readonly action?: Action

  /**
   * Displays an indicator if `true` or the empty string. Otherwise, displays a
   * counter badge if a number or string representing a number is used.
   *
   * @webnative
   */
  @Prop() public readonly badge?: number | string | true

  /**
   * The indicator or counter badge's accessible text that will be used by
   * screen readers.
   */
  @Prop() public readonly badgeDescription?: string

  /**
   * The indicator's status.
   */
  @Prop()
  public readonly badgeIndicatorStatus?: HTMLQdsBadgeIndicatorElement['status'] =
    'neutral'

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * The button's importance.
   */
  @Prop() public readonly importance?: ButtonImportance = 'standard'

  /**
   * The button's size.
   */
  @Prop() public readonly size?: Size | 'hero' = 'standard'

  /**
   * The button's text.
   */
  @Prop() public readonly text!: string

  /**
   * Sets if button is icon only.
   */
  @Prop() public readonly iconOnly: boolean = false

  /**
   * Prevents the button from being interacted with: it cannot be pressed or
   * focused.
   *
   * @webnative
   */
  @Prop()
  public readonly disabled?: boolean

  /**
   * Tells the browser to download the linked file as this filename. Only used
   * when `href` is set.
   *
   * @webnative
   */
  @Prop() public readonly download?: string

  /**
   * The [`<form>`][] element to associate the button with (its form owner).
   *
   * The value of this attribute must be the id of a `<form>` in the same
   * document. If this attribute is not set, the `<qds-button>` is associated
   * with its ancestor `<form>` element, if any.
   *
   * This attribute lets you associate `<qds-button>` elements to `<form>`s
   * anywhere in the document, not just inside a `<form>`. It can also override
   * an ancestor `<form>` element.
   *
   * [`<form>`]: https://developer.mozilla.org/docs/Web/HTML/Element/form
   *
   * @readonly
   * @webnative
   */
  // eslint-disable-next-line unicorn/no-null
  @Prop() public readonly form: ElementInternals['form'] | string = null

  /**
   * The URL that processes the information submitted by the button. Overrides
   * the [`action`](https://developer.mozilla.org/docs/Web/HTML/Element/button#attr-formaction)
   * attribute of the button's form owner. Does nothing if there is no form owner.
   *
   * @webnative
   */
  @Prop({ attribute: 'formaction' }) public readonly formAction?: string

  /**
   * If the button is a submit button (it's inside/associated with a `<form>`
   * and doesn't have `type="button"`), this attribute specifies the
   * [HTTP method][] used to submit the form, or `dialog` which won't submit
   * the form. Possible values:
   *
   * - `get`: The form data are appended to the form's action URL, with a ? as
   * a separator, and the resulting URL is sent to the server. Use this method
   * when the form has no side effects, like search forms.
   * - `post`: The data from the form are included in the body of the HTTP
   * request when sent to the server. Use when the form contains information
   * that shouldn't be public, like login credentials.
   * - `dialog`: Close the dialog box in which the button finds itself, if any,
   * and do not submit the button's form owner.
   *
   * If specified, this attribute overrides the `method` attribute of the
   * button's form owner.
   *
   * [HTTP method]: https://developer.mozilla.org/docs/Web/HTTP/Methods
   *
   * @webnative
   */
  @Prop({ attribute: 'formmethod' }) public readonly formMethod?: FormMethod

  /**
   * If the button is a submit button, this attribute specifies that the form
   * is not to be
   * [validated](https://developer.mozilla.org/docs/Learn/Forms/Form_validation)
   * when it is submitted. If this attribute is specified, it overrides the
   * [`novalidate`](https://developer.mozilla.org/docs/Web/HTML/Element/form#attr-novalidate)
   * attribute of the button's form owner.
   *
   * @webnative
   */
  @Prop({ attribute: 'formnovalidate' })
  public readonly formNoValidate: boolean = false

  /**
   * If the button is a submit button, this attribute is an author-defined name
   * or standardized, underscore-prefixed keyword indicating where to display
   * the response from submitting the form. This is the `name` of, or keyword
   * for, a browsing context (a tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe)).
   * If this attribute is specified, it overrides the
   * [`target`](https://developer.mozilla.org/docs/Web/HTML/Element/form#attr-target)
   * attribute of the button's form owner. The following keywords have special
   * meanings:
   *
   * - `_blank`: Load the response into a new unnamed browsing context — usually
   * a new tab or window, depending on the user's browser settings.
   * - `_parent`: Load the response into the parent browsing context of the
   * current one. If there is no parent, this option behaves the same way as
   * `_self`.
   * - `_self`: Load the response into the same browsing context as the current
   * one.
   * - `_top`: Load the response into the top-level browsing context (that is,
   * the browsing context that is an ancestor of the current one, and has no
   * parent). If there is no parent, this option behaves the same way as
   * `_self`.
   *
   * @webnative
   */
  @Prop({ attribute: 'formtarget' })
  public readonly formTarget: LiteralUnion<ButtonTarget, string> = '_self'

  /**
   * When set, the underlying button will be rendered as an `<a>` with this
   * `href` instead of a `<button>`.
   *
   * @webnative
   */
  @Prop() public readonly href?: string

  /**
   * Returns a list of the [`<label>`][] elements associated with the
   * `qds-button` element.
   *
   * [`<label>`]: https://developer.mozilla.org/docs/Web/HTML/Element/label
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly labels: ElementInternals['labels'] =
    {} as ElementInternals['labels']

  /**
   * When href is define and the button act as a link (`<a>`), the `rel` will be added to the element.
   *
   * @webnative
   */
  @Prop() public readonly rel?: string = 'noopener'

  /**
   * The name of the button, submitted as a pair with the button's `value` as
   * part of the form data, when that button is used to submit the form.
   * Ignored when `href` is set.
   *
   * @webnative
   */
  @Prop() public readonly name?: string

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly target?: LiteralUnion<ButtonTarget, string>

  /**
   * The type of button.
   *
   * `button`: The button has no default behavior, and does nothing when
   * pressed by default. It can have client-side scripts listen to the
   * element's events, which are triggered when the events occur.
   *
   * `submit`: The button submits the form data to the server. This is the
   * default if the attribute is not specified for buttons associated with a
   * `<form>`, or if the attribute is an empty or invalid value.
   *
   * `reset`: The button resets all the controls to their initial values.
   *
   * @webnative
   */
  @Prop() public readonly type?: ButtonType

  /**
   * The error message that would be shown to the user if the `<qds-button>`
   * was to be checked for validity.
   *
   * @readonly
   * @webnative
   */
  @Prop()
  public readonly validationMessage: ElementInternals['validationMessage'] = ''

  /**
   * The [`ValidityState`][] object for this `<qds-button>`.
   *
   * [`ValidityState`]: https://developer.mozilla.org/docs/Web/API/ValidityState
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly validity: ElementInternals['validity'] = VALID_STATE

  /**
   * Defines the value associated with the button's `name` when it's submitted
   * with the form data. This value is passed to the server in params when the
   * form is submitted using this button. Ignored when `href` is set.
   *
   * @webnative
   */
  @Prop() public readonly value?: string

  /**
   * True if `<qds-button>` will be validated when the form is submitted; false
   * otherwise.
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly willValidate: ElementInternals['willValidate'] = false

  /**
   * Emitted when the button loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the button gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @Element() private readonly host!: HTMLQdsButtonElement

  @State() private tabIndex?: number

  @State() private tooltip = false

  @AttachInternals() private readonly internals!: ElementInternals

  #button?: HTMLAnchorElement | HTMLButtonElement

  #span?: HTMLSpanElement

  #ro?: ResizeObserver

  get #buttonType(): ButtonType {
    if (this.type === undefined)
      return this.internals.form ? 'submit' : 'button'
    return this.type
  }

  get #iconOnly(): boolean {
    const hasIcon = this.iconName !== undefined && this.iconName !== ''

    return hasIcon && this.iconOnly
  }

  get #computedAction(): Action | undefined {
    switch (this.action) {
      case 'dropdown':
      case 'dropdown-close':
      case 'next': {
        return this.action
      }
      default: {
        return undefined
      }
    }
  }

  get #computedActionIcon(): Exclude<Action, 'dropdown-close'> | undefined {
    switch (this.action) {
      case 'dropdown':
      case 'next': {
        return this.action
      }
      case 'dropdown-close': {
        return 'dropdown'
      }
      default: {
        return undefined
      }
    }
  }

  get #computedDisabled(): boolean {
    return this.disabled ?? false
  }

  get #computedImportance(): ButtonImportance {
    switch (this.importance) {
      case 'emphasized':
      case 'subdued':
      case 'destructive':
      case 'subdued-destructive': {
        return this.importance
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedSize(): ButtonSize {
    switch (this.size) {
      case 'small':
      case 'large':
      case 'hero': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) {
      event.stopImmediatePropagation()
      return
    }

    if (this.#buttonType === 'button' || !this.internals.form) return
    if (this.#buttonType === 'reset') {
      this.internals.form.reset()
    } else {
      this.internals.form.requestSubmit()
    }
    event.preventDefault()
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    this.internals.ariaDisabled = this.#computedDisabled.toString()
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  @Watch('text')
  protected textChanged(): void {
    this.internals.ariaLabel = this.text

    if (this.#ro) this.#ro.disconnect()
    if (!this.#span) return

    this.tooltip = isOverflowing(this.#span)
    this.#ro = new ResizeObserver(([spanEntry]) => {
      this.tooltip = isOverflowing(spanEntry.target)
    })
    this.#ro.observe(this.#span)
  }

  public componentWillLoad(): void {
    this.internals.role = 'button'
    this.#defineGetter('form', () => this.internals.form)
    this.#defineGetter('willValidate', () => this.internals.willValidate)
    this.#defineGetter('validity', () => this.internals.validity)
    this.#defineGetter(
      'validationMessage',
      () => this.internals.validationMessage,
    )
    this.#defineGetter('labels', () => this.internals.labels)

    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public componentDidLoad(): void {
    this.textChanged()
  }

  public disconnectedCallback(): void {
    if (this.#ro) this.#ro.disconnect()
  }

  public render() {
    const Tag = this.href === undefined ? 'button' : 'a'
    const disabledTabIndex = Tag === 'a' ? -1 : undefined
    const hasBadgeProperties =
      this.badgeDescription !== undefined && this.badge !== undefined
    const ariaLabel = hasBadgeProperties
      ? `${this.text} ${this.badge} ${this.badgeDescription}`
      : this.text

    return (
      <div class="qds-container">
        <Tag
          aria-hidden="true"
          class={{
            'qds-button': true,
            'qds-disabled': Tag === 'a' ? this.#computedDisabled : false,
            'qds-icon-only': this.#iconOnly,
          }}
          data-importance={this.#computedImportance}
          data-size={this.#computedSize}
          data-action={this.#computedAction}
          disabled={Tag === 'a' ? undefined : this.disabled}
          download={Tag === 'a' ? this.download : undefined}
          formAction={this.formAction}
          formMethod={this.formMethod}
          formNoValidate={this.formNoValidate}
          formTarget={this.formTarget}
          href={Tag === 'a' ? this.href : undefined}
          name={Tag === 'a' ? undefined : this.name}
          onBlur={this.#onBlur}
          onFocus={this.#onFocus}
          ref={this.#buttonRef}
          rel={Tag === 'a' ? this.rel : undefined}
          tabIndex={this.#computedDisabled ? disabledTabIndex : this.tabIndex}
          target={Tag === 'a' ? this.target : undefined}
          type={Tag === 'a' ? undefined : this.#buttonType}
          value={Tag === 'a' ? undefined : this.value}
          aria-label={this.#iconOnly ? ariaLabel : undefined}
        >
          {this.iconName !== undefined && this.iconName !== '' && (
            <qds-icon
              aria-hidden="true"
              class="qds-icon"
              data-size={this.#computedSize}
              library={this.iconLibrary}
              name={this.iconName}
            />
          )}
          {!this.#iconOnly && (
            <span
              class="qds-overflow"
              data-size={this.#computedSize}
              ref={this.#spanRef}
            >
              {this.text}
            </span>
          )}
          {isIndicator(this.badge) && (
            <qds-badge-indicator
              class="qds-indicator"
              status={this.badgeIndicatorStatus}
              description={this.badgeDescription}
              size={this.#computedSize}
              strokeRing
            />
          )}
          {isCounter(this.badge) && (
            <qds-badge-counter
              class={{
                'qds-indicator': this.#iconOnly,
              }}
              description={this.badgeDescription}
              importance={this.#computedImportance}
              size={this.#computedSize}
              strokeRing
              value={this.badge}
            />
          )}
          {this.#computedActionIcon !== undefined && (
            <qds-icon
              aria-hidden="true"
              class="qds-icon qds-action"
              data-size={this.#computedSize}
              library="core"
              name={this.#computedActionIcon}
            />
          )}
        </Tag>
        <div
          class={{
            'qds-focus-ring': true,
            'qds-icon-only': this.#iconOnly,
          }}
          data-importance={this.#computedImportance}
          data-size={this.#computedSize}
        />
        {this.tooltip && (
          <qds-tooltip aria-hidden="true" ref={this.#tooltipRef}>
            {this.text}
          </qds-tooltip>
        )}
      </div>
    )
  }

  public checkValidity: ElementInternals['checkValidity'] = () =>
    this.internals.checkValidity()

  public reportValidity: ElementInternals['reportValidity'] = () =>
    this.internals.reportValidity()

  public setCustomValidity: HTMLButtonElement['setCustomValidity'] = (
    error,
  ) => {
    if (error)
      this.internals.setValidity(CUSTOM_ERROR_FLAGS, error, this.#button)
    else this.internals.setValidity(NO_ERROR_FLAGS)
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #buttonRef = (
    button?: HTMLAnchorElement | HTMLButtonElement,
  ): void => {
    this.#button = button
  }

  readonly #spanRef = (span?: HTMLSpanElement): void => {
    this.#span = span
  }

  readonly #tooltipRef = (tooltip?: HTMLQdsTooltipElement): void => {
    if (tooltip)
      // eslint-disable-next-line no-param-reassign
      tooltip.target = this.#button
  }

  #defineGetter(p: PropertyKey, get: () => unknown): void {
    Object.defineProperty(this.host, p, { enumerable: true, get })
  }
}
